import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, BookOpen, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';

const CreateTopicModal = ({ isOpen, onClose, supabase, onSuccess, accountId }) => {
  const [topicName, setTopicName] = useState('');
  const [questionToAsk, setQuestionToAsk] = useState('');
  const [possibleAnswers, setPossibleAnswers] = useState([]);
  const [newAnswer, setNewAnswer] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Reset state when opening
      setTopicName('');
      setQuestionToAsk('');
      setPossibleAnswers([]);
      setNewAnswer('');
      setIsSaving(false);

      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const popupRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleAddAnswer = () => {
    if (!newAnswer.trim()) return;
    if (possibleAnswers.includes(newAnswer.trim())) {
      toast.error("This intent is already in the list.");
      return;
    }
    setPossibleAnswers([...possibleAnswers, newAnswer.trim()]);
    setNewAnswer('');
  };

  const handleRemoveAnswer = (index) => {
    setPossibleAnswers(possibleAnswers.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!topicName.trim()) {
      toast.error('Qualifier Name is required.');
      return;
    }
    if (!questionToAsk.trim()) {
      toast.error('Question is required.');
      return;
    }
    if (possibleAnswers.length === 0) {
      toast.error('At least one possible intent is required.');
      return;
    }

    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('brain_conversation_topics')
        .insert({
          account_id: accountId,
          topic_name: topicName.trim(),
          question_to_ask: questionToAsk.trim(),
          possible_answers: possibleAnswers,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Qualifier saved successfully');
      onSuccess(data); // Pass the newly created topic back
      onClose();
    } catch (err) {
      console.error('Error saving topic:', err);
      toast.error('Failed to save qualifier. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { scale: 1, opacity: 1, y: 0, transition: { type: "spring", damping: 30, stiffness: 300, duration: 0.4 } },
    exit: { scale: 0.98, opacity: 0, y: 10, transition: { duration: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.3, ease: [0.22, 1, 0.36, 1] } }),
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
    removed: { opacity: 0, x: -50, transition: { duration: 0.2 } }
  };

  if (!isOpen) {
    return null;
  }

  const popupContent = (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4"
        onClick={onClose}
      >
        <motion.div
          ref={popupRef}
          variants={popupVariants}
          className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b bg-white z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                <BookOpen size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
              </div>
              <div>
                <h2 className="font-medium text-base sm:text-lg text-gray-900">Create New Qualifier</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Define a qualifier to qualify user intent.</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={16} className="sm:w-[18px] sm:h-[18px]" />
            </motion.button>
          </motion.div>

          {/* Content */}
          <div className="p-5 sm:p-7 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
            <div className="space-y-2">
              <label htmlFor="topicName" className="text-xs sm:text-sm font-medium text-gray-700">Qualifier Name</label>
              <input
                ref={inputRef}
                id="topicName"
                type="text"
                value={topicName}
                onChange={(e) => setTopicName(e.target.value)}
                placeholder="e.g., Service Type, Location"
                className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-black/50"
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="questionToAsk" className="text-xs sm:text-sm font-medium text-gray-700">Question To Decide What To Do</label>
              <input
                id="questionToAsk"
                type="text"
                value={questionToAsk}
                onChange={(e) => setQuestionToAsk(e.target.value)}
                placeholder="e.g., What type of service are you interested in?"
                className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-black/50"
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700">Possible Intents</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAnswer}
                  onChange={(e) => setNewAnswer(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddAnswer()}
                  placeholder="e.g., Wash & Fold"
                  className="flex-1 w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-black/50"
                  disabled={isSaving}
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddAnswer}
                  disabled={!newAnswer.trim() || isSaving}
                  className="h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-xl bg-black text-white disabled:opacity-50"
                >
                  <Plus size={16} className="sm:w-[18px] sm:h-[18px]" />
                </motion.button>
              </div>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <AnimatePresence>
                {possibleAnswers.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center py-10 sm:py-12 px-4 rounded-xl border border-dashed border-gray-200 text-center"
                  >
                    <HelpCircle size={20} className="text-gray-300 mb-2 sm:mb-3 sm:w-6 sm:h-6" />
                    <p className="text-gray-500 text-xs sm:text-sm">No intents added yet.</p>
                  </motion.div>
                ) : (
                  <motion.ul 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-xl overflow-hidden border border-gray-100 divide-y divide-gray-100"
                  >
                    <AnimatePresence>
                      {possibleAnswers.map((answer, index) => (
                        <motion.li 
                          key={`answer-${answer}-${index}`}
                          custom={index}
                          variants={itemVariants}
                          initial="hidden"
                          animate="visible"
                          exit="removed"
                          className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white hover:bg-gray-50"
                        >
                          <span className="text-gray-800 text-xs sm:text-sm font-light mr-2">{answer}</span>
                          <motion.button
                            whileHover={{ scale: 1.1, backgroundColor: '#fef2f2' }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleRemoveAnswer(index)}
                            className="text-gray-500 hover:text-red-500 rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-red-50"
                            disabled={isSaving}
                            aria-label={`Remove ${answer}`}
                          >
                            <Trash2 size={14} className="sm:w-[15px] sm:h-[15px]" />
                          </motion.button>
                        </motion.li>
                      ))}
                    </AnimatePresence>
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Footer */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-5 sm:px-7 py-4 bg-gray-50 flex justify-end gap-2 sm:gap-3 border-t"
          >
            <motion.button
              whileHover={{ scale: 1.02, backgroundColor: '#f9fafb' }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-xs sm:text-sm font-medium bg-white hover:bg-gray-50"
              disabled={isSaving}
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={isSaving}
              className="py-2 sm:py-2.5 px-4 sm:px-5 rounded-xl bg-black text-white text-xs sm:text-sm font-medium disabled:opacity-50"
            >
              {isSaving ? (
                <div className="flex items-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Saving...
                </div>
              ) : (
                "Save Qualifier"
              )}
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(popupContent, document.body);
};

export default CreateTopicModal;
